import React, { useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { FileText, FileDown, Shield, Upload, AlertTriangle, CheckCircle2, Info } from "lucide-react";

/**
 * Block FSE 2.0 ready: visibile solo per visite chiuse non archiviate.
 * Mostra stato FSE, pulsanti Prepara/Scarica/Carica firmato/Validation report.
 */
export default function FseBlock({ vid }) {
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/visits/${vid}/fse/validation`);
      setMeta(data.metadata);
    } catch (e) {
      toast.error(apiErr(e, "Errore validazione FSE"));
    } finally {
      setBusy(false);
    }
  };

  const prepare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/visits/${vid}/fse/prepare`);
      setMeta(data.metadata);
      toast.success("Pacchetto FSE preparato (locale)");
    } catch (e) {
      toast.error(apiErr(e, "Errore preparazione pacchetto FSE"));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/visits/${vid}/fse/package.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `primevision-fse-${vid}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Pacchetto FSE scaricato");
    } catch (e) {
      toast.error(apiErr(e, "Errore download"));
    } finally {
      setBusy(false);
    }
  };

  const uploadSigned = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f, f.name);
      const { data } = await api.post(`/visits/${vid}/fse/upload-signed`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`PDF firmato registrato (sha256: ${(data.sha256 || "").slice(0, 12)}…)`);
      await refresh();
    } catch (err) {
      toast.error(apiErr(err, "Errore upload PDF firmato"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const stateColor = (st) => {
    if (!st) return "bg-slate-100 text-slate-600 border-slate-200";
    if (st.includes("non valida") || st.includes("failed")) return "bg-red-100 text-red-700 border-red-200";
    if (st.includes("Pronto") || st.includes("publish")) return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (st.includes("firmato") || st.includes("signed")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-amber-50 text-amber-700 border-amber-200";
  };

  return (
    <div className="border border-[#E2E8F0] rounded-xl p-4 bg-white" data-testid="fse-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-brand-azure" />
          <h3 className="font-extrabold text-brand-navy text-sm">FSE 2.0 — Pacchetto pronto al gateway</h3>
          <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">RSA</span>
        </div>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${stateColor(meta?.state || meta?.fse_status)}`} data-testid="fse-state-badge">
          {meta?.state || (meta?.fse_status ? meta.fse_status : "Non preparato")}
        </span>
      </div>

      <div className="text-xs text-slate-600 mb-3 flex items-start gap-1.5">
        <Info size={12} className="mt-0.5 shrink-0" />
        <span>FSE-ready non equivale a pubblicazione FSE ufficiale. Genera CDA R2 + PDF + manifest + checksum SHA-256 + validation report locale. <b>Nessun invio</b> al Gateway senza certificati e provider configurati.</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button data-testid="fse-prepare-btn" disabled={busy} onClick={prepare} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-brand-navy hover:bg-brand-navyDark text-white flex items-center gap-1.5 disabled:opacity-60">
          <FileText size={13} /> Prepara pacchetto FSE
        </button>
        <button data-testid="fse-download-btn" disabled={busy} onClick={download} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-brand-azure hover:bg-brand-azure/90 text-white flex items-center gap-1.5 disabled:opacity-60">
          <FileDown size={13} /> Scarica pacchetto (.zip)
        </button>
        <label className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 hover:bg-slate-200 text-brand-navy flex items-center gap-1.5 cursor-pointer">
          <Upload size={13} /> Carica PDF firmato (PAdES)
          <input ref={fileRef} type="file" accept="application/pdf" onChange={uploadSigned} className="hidden" data-testid="fse-upload-input" />
        </label>
        <button data-testid="fse-validation-btn" disabled={busy} onClick={() => { setShowReport(true); refresh(); }} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-[#E2E8F0] text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-60">
          Validation report
        </button>
      </div>

      {meta && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5">
            {meta.pades_status === "firmato" ? <CheckCircle2 size={12} className="text-emerald-600" /> : <AlertTriangle size={12} className="text-amber-500" />}
            <span>Firma PAdES: <b>{meta.pades_status || "non firmato"}</b></span>
          </div>
          <div className="flex items-center gap-1.5">
            {meta.pdfa_status === "verificato" ? <CheckCircle2 size={12} className="text-emerald-600" /> : <AlertTriangle size={12} className="text-slate-400" />}
            <span>PDF/A: <b>{meta.pdfa_status || "non verificato"}</b></span>
          </div>
          {meta.pdf_sha256 && <div className="text-slate-500 font-mono">PDF sha256: {meta.pdf_sha256.slice(0, 24)}…</div>}
          {meta.cda_sha256 && <div className="text-slate-500 font-mono">CDA sha256: {meta.cda_sha256.slice(0, 24)}…</div>}
        </div>
      )}

      {showReport && meta && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowReport(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-5 shadow-2xl max-h-[80vh] overflow-auto" data-testid="fse-validation-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-extrabold text-brand-navy">Validation report (locale)</h3>
              <button onClick={() => setShowReport(false)} className="text-slate-500 hover:text-brand-navy">✕</button>
            </div>
            <div className={`text-sm font-bold px-3 py-2 rounded-lg ${stateColor(meta.state)}`}>{meta.state}</div>
            {meta.errors?.length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-wider font-bold text-red-700 mb-1">Errori bloccanti</div>
                <ul className="text-xs text-red-800 list-disc list-inside space-y-0.5">{meta.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
            {meta.warnings?.length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-1">Warning</div>
                <ul className="text-xs text-amber-800 list-disc list-inside space-y-0.5">{meta.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}
            <div className="mt-3 text-[11px] text-slate-500">
              Validato localmente: {meta.validated_at_local || meta.generated_at} · Gateway: {meta.gateway_activity || "NONE"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
